import { useCallback, useRef } from 'react'
import { taskMenuMarks, type TaskMenuMarks, type TaskMenuMarkSource } from '../components/TaskMenu/taskMenuMarks'

interface UseContextMenuMarksParams {
  /** Dieselbe Quelle wie in `useContextMenuBadgeUpdate` — die beiden müssen
   *  dieselbe Auswahl sehen, sonst hakt das Menü etwas an, das es gar nicht
   *  ändert. */
  getSelection: () => Set<string>
  /** Alle Aufgaben der Seite; daraus werden die ausgewählten herausgesucht. */
  tasks: readonly (TaskMenuMarkSource & { id: string })[]
}

/**
 * Die **Haken** im Aufgaben-Menü: Welche Eigenschaften trägt die ganze Auswahl?
 *
 * Die Regel, welche Aufgaben gemeint sind, steht hier ein zweites Mal — sie
 * muss Wort für Wort dieselbe sein wie in `useContextMenuBadgeUpdate`: Eine
 * Mehrfachauswahl zählt nur, wenn die angeklickte Aufgabe dazugehört; sonst
 * zählt sie allein. Stünden die beiden auseinander, hakte das Menü etwas an,
 * das es gar nicht setzen würde.
 */
export function useContextMenuMarks({ getSelection, tasks }: UseContextMenuMarksParams) {
  const getSelectionRef = useRef(getSelection)
  getSelectionRef.current = getSelection
  const tasksRef = useRef(tasks)
  tasksRef.current = tasks

  return useCallback((clickedTaskId: string): TaskMenuMarks => {
    const selection = getSelectionRef.current()
    const all = tasksRef.current
    const targets = selection.size > 1 && selection.has(clickedTaskId)
      ? all.filter(task => selection.has(task.id))
      : all.filter(task => task.id === clickedTaskId)
    return taskMenuMarks(targets)
  }, [])
}
